import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import RestHttpClient, { renderValidationErrors } from '../../../../../common/RestHttpClient';
import {
  TakeoverDispatchDate,
  TakeoverPosition,
  TakeoverPositions,
  UpdateHandOverRequest,
} from '../../../../../generated/ApiClient';
import { EnhancedDialog } from '../../../../../component/EnhancedDialog';
import { FormDatePicker } from '../../../../../component/form/FormDatePicker';
import { usePermissions } from '../../../../../common/auth/PermissionsContext';
import { pushNotificationHandler } from '../../../../../common/PushNotification';
import { FleetHandOverRow } from '../../fleet/FleetHandOverRow';
import {
  handOverPositionsSchema,
  HandOverPositionsValues,
  validPrintStatus,
} from '../../fleet/fleetHandOverSchema';
import {
  isTakeoverRunning,
  TakeoverFinalAction,
  TakeoverProgressPanel,
  TakeoverStatus,
} from './TakeoverProgressPanel';
import { getServerErrorMessage, isForbidden } from './useTakeoverFlow';

const schema = z.object({
  expectedDispatchDate: z.date(),
});

type TakeoverDispatchDateFormValues = z.infer<typeof schema>;

export const toUtcMidnight = (date: Date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

interface Props {
  dealId: number;
  calculationId?: number;
  isFleet?: boolean;
  status: TakeoverStatus | null;
  onClose: () => void;
  onStart: (finalAction: TakeoverFinalAction) => void;
  onShowSignDocuments: () => void;
}

export function TakeoverDispatchDateDialog({
  dealId,
  calculationId,
  isFleet = false,
  status,
  onClose,
  onStart,
  onShowSignDocuments,
}: Props) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canPrintDocuments =
    hasPermission('LeasingProducts', 'Print') ||
    hasPermission('FinancingProducts', 'Print') ||
    hasPermission('Services', 'Print');
  const canSign = hasPermission('SignatureDocument', 'Execute') && canPrintDocuments;
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [positionsLoaded, setPositionsLoaded] = useState(false);
  const [dialogError, setDialogError] = useState<string>();
  const isOpen = calculationId !== undefined;
  const startedRef = useRef(false);

  const dateForm = useForm<TakeoverDispatchDateFormValues>({
    resolver: zodResolver(schema),
  });
  const positionsForm = useForm<HandOverPositionsValues>({
    resolver: zodResolver(handOverPositionsSchema),
    defaultValues: { positions: [] },
  });
  const positions = useWatch({ control: positionsForm.control, name: 'positions' }) ?? [];

  const running = isTakeoverRunning(status);
  const flowStarted = status !== null;
  const locked = loading || flowStarted;
  const hasSelection = !isFleet || positions.some((p) => p.selected);
  const signDocumentsAvailable = status?.finalAction === 'sign' && status.document === 'done';
  const handOverAvailable = isFleet && status?.document === 'done';

  const handleCancel = () => {
    if (!loading && !running && !handingOver) onClose();
  };

  const loadDispatchDate = async (calcId: number) => {
    const { data } = await RestHttpClient.getTakeoverDispatchDate(dealId, calcId);
    const stored = data.expectedDispatchDate;
    dateForm.reset({ expectedDispatchDate: stored ? toUtcMidnight(stored) : undefined });
  };

  const loadPositions = async () => {
    const { data } = await RestHttpClient.getFleetHandOver(dealId);
    setPositionsLoaded(true);
    positionsForm.reset({
      positions: data.map((x) => ({
        position: x.position,
        vin: x.vin ?? '',
        cmsId: x.cmsId ?? '',
        status: x.status,
        selected: !!x.vin && validPrintStatus.includes(x.status),
      })),
    });
  };

  useEffect(() => {
    startedRef.current = false;
    dateForm.reset({ expectedDispatchDate: undefined });
    positionsForm.reset({ positions: [] });
    setPositionsLoaded(false);
    setDialogError(undefined);
    setLoadFailed(false);
    if (calculationId === undefined) return;

    setLoading(true);
    (isFleet ? loadPositions() : loadDispatchDate(calculationId))
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, calculationId, isFleet]);

  const saveAndStart = (finalAction: TakeoverFinalAction) => () => {
    if (calculationId === undefined || startedRef.current) return;
    startedRef.current = true;

    const allowRestart = () => {
      startedRef.current = false;
    };
    setDialogError(undefined);

    const startWhenSaved = async (
      save: () => Promise<{ data: boolean }>,
      showRejection: (rejection: unknown) => void
    ) => {
      setLoading(true);
      try {
        const result = await save();
        if (result.data) {
          onStart(finalAction);
        } else {
          allowRestart();
          showRejection(result);
        }
      } catch (err) {
        allowRestart();
        if (isForbidden(err)) setDialogError(t('errors.calculation.takeover.forbidden'));
        else showRejection(err);
      } finally {
        setLoading(false);
      }
    };

    if (isFleet) {
      positionsForm.handleSubmit(
        (values) =>
          startWhenSaved(
            () =>
              RestHttpClient.saveTakeoverPositions(
                dealId,
                calculationId,
                new TakeoverPositions({
                  positions: values.positions.map(
                    (p) =>
                      new TakeoverPosition({
                        position: p.position,
                        vin: p.vin ?? undefined,
                        selected: p.selected,
                      })
                  ),
                })
              ),
            (rejection) => renderValidationErrors(rejection, positionsForm.setError)
          ),
        allowRestart
      )();
      return;
    }

    dateForm.handleSubmit(
      (values) =>
        startWhenSaved(
          () =>
            RestHttpClient.setTakeoverDispatchDate(
              dealId,
              calculationId,
              new TakeoverDispatchDate({
                expectedDispatchDate: toUtcMidnight(values.expectedDispatchDate),
              })
            ),
          (rejection) => renderValidationErrors(rejection, dateForm.setError)
        ),
      allowRestart
    )();
  };

  const handOver = async () => {
    setHandingOver(true);
    setDialogError(undefined);
    try {
      const result = await RestHttpClient.updateHandOver(new UpdateHandOverRequest({ dealId }));
      if (result.data) {
        pushNotificationHandler.publish(t('deals.fleet.handOverSuccess'), 'success');
        onClose();
        return;
      }
      setDialogError(
        getServerErrorMessage(result.messages) ?? t('errors.calculation.takeover.handOverFailed')
      );
    } catch (err) {
      setDialogError(
        isForbidden(err)
          ? t('errors.calculation.takeover.forbidden')
          : t('errors.calculation.takeover.handOverFailed')
      );
    } finally {
      setHandingOver(false);
    }
  };

  const noPositions = isFleet && positionsLoaded && positions.length === 0;

  return (
    <EnhancedDialog
      maxWidth={isFleet ? 'lg' : 'md'}
      fullWidth
      isDialogOpened={isOpen}
      setIsDialogOpened={handleCancel}
      handleCancel={handleCancel}
    >
      <DialogTitle>
        {isFleet ? t('deals.takeoverDispatchDate.fleetTitle') : t('deals.takeoverDispatchDate.title')}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={4}>
          <Grid item xs={12} md={isFleet ? 8 : 5}>
            {loadFailed && (
              <Alert severity="error">
                {isFleet
                  ? t('deals.takeoverDispatchDate.positionsLoadFailed')
                  : t('deals.takeoverDispatchDate.loadFailed')}
              </Alert>
            )}
            {noPositions && (
              <Alert severity="info">{t('deals.takeoverDispatchDate.noPositions')}</Alert>
            )}
            {isFleet ? (
              <Stack gap={2} sx={{ paddingY: 3 }}>
                {positions.map((field, index) => (
                  <FleetHandOverRow
                    key={field.position}
                    control={positionsForm.control}
                    index={index}
                    field={field}
                    disabled={locked}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ paddingY: 3 }}>
                <FormDatePicker
                  control={dateForm.control}
                  name="expectedDispatchDate"
                  label={t('deals.takeoverDispatchDate.expectedDispatchDate')}
                  disabled={locked || loadFailed}
                  required
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={isFleet ? 4 : 7}>
            <TakeoverProgressPanel status={status} />
          </Grid>
        </Grid>
        {dialogError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {dialogError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          {flowStarted ? (
            <>
              {signDocumentsAvailable && (
                <Button onClick={onShowSignDocuments} sx={{ marginRight: '4px' }}>
                  {t('deals.takeoverDispatchDate.showSignDocuments')}
                </Button>
              )}
              {handOverAvailable && (
                <Button
                  onClick={handOver}
                  disabled={handingOver}
                  variant="contained"
                  sx={{ marginRight: '4px' }}
                >
                  {t('deals.takeoverDispatchDate.handOver')}
                </Button>
              )}
              <Button onClick={handleCancel} disabled={running || handingOver} variant="contained">
                {t('deals.takeoverStatus.close')}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleCancel}
                disabled={loading}
                sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
              >
                {t('back')}
              </Button>
              <Button
                onClick={saveAndStart('print')}
                variant="contained"
                disabled={locked || loadFailed || !hasSelection}
                sx={{ marginRight: '4px' }}
              >
                {t('deals.takeoverDispatchDate.print')}
              </Button>
              {canSign && (
                <Button
                  onClick={saveAndStart('sign')}
                  variant="contained"
                  disabled={locked || loadFailed || !hasSelection}
                >
                  {t('deals.takeoverDispatchDate.sign')}
                </Button>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
}
